import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/site/Home";
import HowItWorks from "@/pages/site/HowItWorks";
import About from "@/pages/site/About";
import Faq from "@/pages/site/Faq";
import Contact from "@/pages/site/Contact";
import Register from "@/pages/site/Register";
import SalonLogin from "@/pages/site/SalonLogin";
import BusinessLogin from "@/pages/site/BusinessLogin";
import RecruitmentPage from "@/pages/site/RecruitmentPage";
import AdminLogin from "@/pages/AdminLogin";
import SuperAdmin from "@/pages/SuperAdmin";
import BusinessAdmin from "@/pages/BusinessAdmin";
import SalonAdmin from "@/pages/SalonAdmin";
import CustomerBooking from "@/pages/CustomerBooking";
import BookingSuccess from "@/pages/BookingSuccess";
import AuthCallback from "@/pages/AuthCallback";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/salon/login" replace />;
  if (role && user.role !== role) {
    const home = user.role === "super_admin" ? "/admin/super" : user.role === "business_admin" ? "/business" : "/admin/salon";
    return <Navigate to={home} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Process Google OAuth return FIRST (synchronously) to avoid race conditions.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
      </Route>
      <Route path="/register" element={<Register />} />
      <Route path="/salon/login" element={<SalonLogin />} />
      <Route path="/business/login" element={<BusinessLogin />} />
      <Route path="/partner/:slug" element={<RecruitmentPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin/super" element={<Protected role="super_admin"><SuperAdmin /></Protected>} />
      <Route path="/business" element={<Protected role="business_admin"><BusinessAdmin /></Protected>} />
      <Route path="/admin/salon" element={<Protected role="salon_admin"><SalonAdmin /></Protected>} />
      <Route path="/book/:slug" element={<CustomerBooking />} />
      <Route path="/book/:slug/success" element={<BookingSuccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </SiteProvider>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
